import {isFeatureEnabled} from 'feature-flags';
import {Form} from 'platform/components';

import {useCallback, useMemo} from 'react';

import {isNil, isNotNil} from 'ramda';

import {TestIdProps, useDebouncedCallback, useQueryState} from 'shared/web';

import {FilterContextType, FilterProvider} from '../../hooks/FilterContext';
import {useSelectedFilters} from '../../hooks/SelectedFiltersContext';
import {usePriceReport} from '../../hooks/usePriceReport';
import {usePriceReportSearchParams} from '../../hooks/usePriceReportSearchParams';
import {FilterFormData} from '../../types/FilterFormData';
import {featureFlags} from '../../utils/featureFlags';
import {getDefaultValues} from '../../utils/getDefaultValues';
import {QueryStateKeys} from '../../utils/routing';
import {validateFilterValues} from '../../utils/validateFilterValues';
import {formValuesToRequestValues} from '../Filter/utils/formValuesToRequestValues';
import {FilterLayoutForm} from './components/FilterLayoutForm';

export function FilterLayout(props: TestIdProps) {
  const {priceReport, requestData} = usePriceReport();
  const {selectedFilters, onSelectFilters} = useSelectedFilters();
  const {resetPage} = usePriceReportSearchParams();
  const [, , clearDebug] = useQueryState(QueryStateKeys.Debug);

  const changeHandler = useCallback(
    (context: FilterContextType, values: FilterFormData) => {
      const errors = validateFilterValues(values);
      context.setErrors(errors);

      if (isNil(errors)) {
        context.onFilterChange(formValuesToRequestValues(values));
        onSelectFilters(values);
        resetPage();
      }
    },
    [resetPage, onSelectFilters]
  );

  const debouncedChangeHandler = useDebouncedCallback(changeHandler, 500);

  const defaultValues = useMemo(() => getDefaultValues(priceReport), [priceReport]);

  const showSavedFilters =
    isFeatureEnabled(featureFlags.PR_RELATIVE_FILTERS) && isNotNil(requestData?.customerId);

  if (!priceReport) {
    return null;
  }

  return (
    <FilterProvider initialFilter={formValuesToRequestValues(selectedFilters ?? defaultValues)}>
      {(context) => (
        <Form<FilterFormData>
          defaultValues={selectedFilters ?? defaultValues}
          onChange={(values) => {
            debouncedChangeHandler(context, values);
          }}
        >
          {(control, formApi) => (
            <FilterLayoutForm
              clearDebug={clearDebug}
              context={context}
              control={control}
              formApi={formApi}
              onSelectFilters={onSelectFilters}
              priceReport={priceReport}
              showSavedFilters={showSavedFilters}
              data-testid={props['data-testid']}
            />
          )}
        </Form>
      )}
    </FilterProvider>
  );
}
